import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface BreadcrumbItem {
  label: string;
  url?: string;
}

const ROUTE_LABELS: Record<string, string> = {
  'dashboard':          'Dashboard',
  'transactions':       'Lançamentos',
  'categories':         'Categorias',
  'accounts-payable':   'Contas a Pagar',
  'accounts':           'Gestão de Contas',
  'credit-card':        'Cartão de Crédito',
  'invoices':           'Faturas',
  'clients':            'Clientes',
  'employees':          'Funcionários',
  'service-providers':  'Prestadores de Serviço',
  'birthdays':          'Aniversariantes',
  'users':              'Usuários',
  'roles':              'Perfis de Acesso',
  'settings':           'Configurações',
  'reports':            'Relatórios',
  'operation-reports':  'Relatórios de Operação',
  'logs':               'Logs do Sistema',
  'sql-editor':         'SQL Editor',
  'collaborators':      'Colaboradores',
  'test-runner':        'Test Runner',
};

// Maps a route segment to its sidebar group label
const ROUTE_GROUP: Record<string, string> = {
  'transactions':      'Financeiro',
  'accounts-payable':  'Financeiro',
  'accounts':          'Financeiro',
  'credit-card':       'Financeiro',
  'categories':        'Financeiro',
  'clients':           'Clientes & Faturas',
  'invoices':          'Clientes & Faturas',
  'operation-reports': 'Clientes & Faturas',
  'employees':         'Recursos Humanos',
  'service-providers': 'Recursos Humanos',
  'birthdays':         'Recursos Humanos',
  'collaborators':     'Recursos Humanos',
  'users':             'Usuários',
  'roles':             'Usuários',
  'settings':          'Sistema',
  'logs':              'Sistema',
  'sql-editor':        'Sistema',
  'test-runner':       'Sistema',
};

@Component({
  selector: 'app-breadcrumb',
  template: `
    <nav class="breadcrumb" aria-label="breadcrumb">
      <a class="breadcrumb-home" routerLink="/dashboard">
        <mat-icon>home</mat-icon>
        <span class="breadcrumb-root">NGX Hub</span>
      </a>
      <ng-container *ngFor="let item of items; let last = last">
        <mat-icon class="breadcrumb-sep">chevron_right</mat-icon>
        <a *ngIf="item.url && !last" class="breadcrumb-link" [routerLink]="item.url">{{ item.label }}</a>
        <span *ngIf="!item.url || last" class="breadcrumb-current">{{ item.label }}</span>
      </ng-container>
      <ng-container *ngIf="items.length === 0">
        <mat-icon class="breadcrumb-sep">chevron_right</mat-icon>
        <span class="breadcrumb-current">{{ pageTitle }}</span>
      </ng-container>
    </nav>
  `,
  styles: [`
    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 2px;
      font-size: 12px;
      color: var(--ngx-text-tertiary, #888);
    }
    .breadcrumb-home {
      display: flex;
      align-items: center;
      gap: 4px;
      color: var(--ngx-text-tertiary, #888);
      text-decoration: none;
      transition: color 0.15s;
      mat-icon { font-size: 15px; width: 15px; height: 15px; }
      &:hover { color: var(--ngx-text-primary); }
    }
    .breadcrumb-root { font-size: 12px; }
    .breadcrumb-sep {
      font-size: 14px;
      width: 14px;
      height: 14px;
      opacity: 0.5;
    }
    .breadcrumb-link {
      color: var(--ngx-text-tertiary, #888);
      text-decoration: none;
      transition: color 0.15s;
      &:hover { color: var(--ngx-text-primary); }
    }
    .breadcrumb-current {
      color: var(--ngx-text-secondary);
      font-weight: 500;
    }
  `],
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  items: BreadcrumbItem[] = [];
  /** Label of the current page — the single source used for the header band H1. */
  pageTitle = '';
  private sub!: Subscription;

  constructor(private router: Router, private activatedRoute: ActivatedRoute) {}

  ngOnInit(): void {
    this.build();
    this.sub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.build());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private build(): void {
    const segments = this.router.url.split('?')[0].split('/').filter(Boolean);
    const AUTH_PATHS = ['login', 'mfa-verify', 'mfa-required', 'forgot-password', 'reset-password'];
    if (segments.length === 0 || AUTH_PATHS.includes(segments[0])) {
      this.items = [];
      this.pageTitle = '';
      return;
    }

    // Current page label — same source the crumbs use. Drives the header band H1.
    const lastSeg = segments[segments.length - 1];
    this.pageTitle = ROUTE_LABELS[lastSeg] || this.capitalize(lastSeg);

    // Skip the crumb list on dashboard — "Home > Dashboard" adds no value — but the
    // pageTitle above still feeds the header H1.
    if (segments.length === 1 && segments[0] === 'dashboard') {
      this.items = [];
      return;
    }

    const items: BreadcrumbItem[] = [];

    // Inject sidebar group as first crumb when the page belongs to one
    const firstSeg = segments[0];
    const group = ROUTE_GROUP[firstSeg];
    if (group) {
      items.push({ label: group });
    }

    // Build remaining crumbs from URL segments
    segments.forEach((seg, i) => {
      items.push({
        label: ROUTE_LABELS[seg] || this.capitalize(seg),
        url: i < segments.length - 1 ? '/' + segments.slice(0, i + 1).join('/') : undefined,
      });
    });

    this.items = items;
  }

  private capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
  }
}
